import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';
import { fetchLocalidadesCatalogo, LocalidadOption } from '../../../utils/localidadesCatalogo';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const isImagePath = (value: string) => /\.(png|jpe?g|webp|gif)$/i.test(value);

const FormularioPanFiesta: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    artesanoId: '',
    familia: '',
    taller: '',
    direccionTaller: '',
    localidad: '',
    referencia: '',
    telefono: '',
    correo: '',
    aniosExp: '',
    tipoHorno: '',
    prodDiaria: '',
    prodAlta: '',
    numPersonas: '',
    productos: '',
    temporadas: '',
    participaFerias: false,
    requiereApoyo: false,
    apoyoDesc: '',
    obs: '',
    estatus: 'activo',
  });
  const [guardando, setGuardando] = useState(false);
  const [artesanos, setArtesanos] = useState<any[]>([]);
  const [localidades, setLocalidades] = useState<LocalidadOption[]>([]);
  const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
  const [fotosNuevas, setFotosNuevas] = useState<File[]>([]);
  const [ineActual, setIneActual] = useState('');
  const [ineNueva, setIneNueva] = useState<File | null>(null);

  useEffect(() => {
    axiosInstance
      .get('/turismo/artesanos')
      .then((res) => setArtesanos(res.data || []))
      .catch(() => setArtesanos([]));
    fetchLocalidadesCatalogo().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  useEffect(() => {
    if (!data) return;
    setForm({
      artesanoId: data.artesanoId || '',
      familia: data.familia || '',
      taller: data.taller || '',
      direccionTaller: data.direccionTaller || '',
      localidad: data.localidad || '',
      referencia: data.referencia || '',
      telefono: data.telefono || '',
      correo: data.correo || '',
      aniosExp: data.aniosExp ?? '',
      tipoHorno: data.tipoHorno || '',
      prodDiaria: data.prodDiaria || '',
      prodAlta: data.prodAlta || '',
      numPersonas: data.numPersonas ?? '',
      productos: Array.isArray(data.productos) ? data.productos.join(', ') : data.productos || '',
      temporadas: Array.isArray(data.temporadas) ? data.temporadas.join(', ') : data.temporadas || '',
      participaFerias: !!data.participaFerias,
      requiereApoyo: !!data.requiereApoyo,
      apoyoDesc: data.apoyoDesc || '',
      obs: data.obs || '',
      estatus: data.estatus || 'activo',
    });
    setFotosExistentes(Array.isArray(data.fotos) ? data.fotos.filter(Boolean) : []);
    setFotosNuevas([]);
    setIneActual(data.ineUrl || '');
    setIneNueva(null);
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const toArray = (value: string) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const handleFotosSeleccionadas = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setFotosNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleIneSeleccionada = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIneNueva(e.target.files?.[0] || null);
  };

  const handleSubmit = async () => {
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('artesanoId', form.artesanoId ? String(Number(form.artesanoId)) : '');
      fd.append('familia', form.familia || '');
      fd.append('taller', form.taller || '');
      fd.append('direccionTaller', form.direccionTaller || '');
      fd.append('localidad', form.localidad || '');
      fd.append('referencia', form.referencia || '');
      fd.append('telefono', form.telefono || '');
      fd.append('correo', form.correo || '');
      fd.append('aniosExp', form.aniosExp || '');
      fd.append('tipoHorno', form.tipoHorno || '');
      fd.append('prodDiaria', form.prodDiaria || '');
      fd.append('prodAlta', form.prodAlta || '');
      fd.append('numPersonas', form.numPersonas || '');
      fd.append('productos', JSON.stringify(form.productos ? toArray(form.productos) : []));
      fd.append('temporadas', JSON.stringify(form.temporadas ? toArray(form.temporadas) : []));
      fd.append('participaFerias', String(!!form.participaFerias));
      fd.append('requiereApoyo', String(!!form.requiereApoyo));
      fd.append('apoyoDesc', form.apoyoDesc || '');
      fd.append('obs', form.obs || '');
      fd.append('estatus', form.estatus || 'activo');
      fd.append('fotosExistentes', JSON.stringify(fotosExistentes));
      fd.append('ineActual', ineActual || '');

      fotosNuevas.forEach((file) => fd.append('fotos', file));
      if (ineNueva) fd.append('identificacionIne', ineNueva);

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/pan-fiesta/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/pan-fiesta', fd);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel>Artesano relacionado</FormLabel>
          <Select value={form.artesanoId} onChange={(e) => handleChange('artesanoId', e.target.value)} isDisabled={disabled}>
            <option value="">Sin asignar</option>
            {artesanos.map((a) => (
              <option key={a.id} value={a.id}>{a.nombres} {a.apellidos}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Familia</FormLabel>
          <Input value={form.familia} onChange={(e) => handleChange('familia', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Taller</FormLabel>
          <Input value={form.taller} onChange={(e) => handleChange('taller', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Direccion taller</FormLabel>
          <Input value={form.direccionTaller} onChange={(e) => handleChange('direccionTaller', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Localidad</FormLabel>
          <Select value={form.localidad} onChange={(e) => handleChange('localidad', e.target.value)} isDisabled={disabled}>
            <option value="">Selecciona localidad</option>
            {localidades.map((loc) => (
              <option key={loc.id} value={loc.label}>{loc.label}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Referencia</FormLabel>
          <Input value={form.referencia} onChange={(e) => handleChange('referencia', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Telefono</FormLabel>
          <Input value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Correo</FormLabel>
          <Input value={form.correo} onChange={(e) => handleChange('correo', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Anios de experiencia</FormLabel>
          <Input type="number" value={form.aniosExp} onChange={(e) => handleChange('aniosExp', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Tipo de horno</FormLabel>
          <Input value={form.tipoHorno} onChange={(e) => handleChange('tipoHorno', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Produccion diaria</FormLabel>
          <Input value={form.prodDiaria} onChange={(e) => handleChange('prodDiaria', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Produccion alta</FormLabel>
          <Input value={form.prodAlta} onChange={(e) => handleChange('prodAlta', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Numero de personas</FormLabel>
          <Input type="number" value={form.numPersonas} onChange={(e) => handleChange('numPersonas', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Productos (coma)</FormLabel>
          <Textarea value={form.productos} onChange={(e) => handleChange('productos', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Temporadas (coma)</FormLabel>
          <Textarea value={form.temporadas} onChange={(e) => handleChange('temporadas', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Participa en ferias</FormLabel>
          <Checkbox isChecked={form.participaFerias} onChange={(e) => handleChange('participaFerias', e.target.checked)} isDisabled={disabled}>
            Si
          </Checkbox>
        </FormControl>
        <FormControl>
          <FormLabel>Requiere apoyo</FormLabel>
          <Checkbox isChecked={form.requiereApoyo} onChange={(e) => handleChange('requiereApoyo', e.target.checked)} isDisabled={disabled}>
            Si
          </Checkbox>
        </FormControl>
        <FormControl>
          <FormLabel>Descripcion de apoyo</FormLabel>
          <Textarea value={form.apoyoDesc} onChange={(e) => handleChange('apoyoDesc', e.target.value)} isDisabled={disabled} />
        </FormControl>

        <FormControl>
          <FormLabel>Subir fotos</FormLabel>
          {!disabled ? (
            <>
              <Input type="file" accept="image/*" multiple onChange={handleFotosSeleccionadas} p={1} />
              <FormHelperText>Sube imágenes del taller/producto, no URLs.</FormHelperText>
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">Solo lectura</Text>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>INE / Identificación</FormLabel>
          {!disabled ? (
            <>
              <Input type="file" accept=".pdf,image/*" onChange={handleIneSeleccionada} p={1} />
              <FormHelperText>PDF o imagen.</FormHelperText>
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">Solo lectura</Text>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>Observaciones</FormLabel>
          <Textarea value={form.obs} onChange={(e) => handleChange('obs', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Estatus</FormLabel>
          <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <Stack spacing={4} mt={5}>
        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>Fotos registradas</Text>
          {fotosExistentes.length === 0 ? (
            <Text fontSize="sm" color="gray.500">Sin fotos registradas.</Text>
          ) : (
            <Wrap>
              {fotosExistentes.map((foto) => (
                <WrapItem key={foto}>
                  <Tag colorScheme="orange" borderRadius="full">
                    <TagLabel>
                      <Link href={resolveAssetUrl(foto)} isExternal>{foto.split('/').pop()}</Link>
                    </TagLabel>
                    {!disabled && <TagCloseButton onClick={() => setFotosExistentes((prev) => prev.filter((f) => f !== foto))} />}
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
          {fotosExistentes.filter(isImagePath).length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
              {fotosExistentes.filter(isImagePath).map((foto) => (
                <Box key={`${foto}-img`} borderWidth="1px" borderRadius="md" overflow="hidden">
                  <Image src={resolveAssetUrl(foto)} alt={foto} h="120px" w="100%" objectFit="cover" />
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>Nuevas fotos por subir</Text>
          {fotosNuevas.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No hay fotos nuevas seleccionadas.</Text>
          ) : (
            <Wrap>
              {fotosNuevas.map((file, index) => (
                <WrapItem key={`${file.name}-${index}`}>
                  <Tag colorScheme="blue" borderRadius="full">
                    <TagLabel>{file.name}</TagLabel>
                    {!disabled && <TagCloseButton onClick={() => setFotosNuevas((prev) => prev.filter((_, i) => i !== index))} />}
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>INE registrado</Text>
          {ineActual ? (
            <Tag colorScheme="purple" borderRadius="full">
              <TagLabel>
                <Link href={resolveAssetUrl(ineActual)} isExternal>{ineActual.split('/').pop()}</Link>
              </TagLabel>
              {!disabled && <TagCloseButton onClick={() => setIneActual('')} />}
            </Tag>
          ) : (
            <Text fontSize="sm" color="gray.500">Sin archivo registrado.</Text>
          )}
          {ineNueva && (
            <Box mt={2}>
              <Tag colorScheme="blue" borderRadius="full">
                <TagLabel>{ineNueva.name}</TagLabel>
                {!disabled && <TagCloseButton onClick={() => setIneNueva(null)} />}
              </Tag>
            </Box>
          )}
        </Box>
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioPanFiesta;
